/**
 * จอเกม — **หน้าเดียวใช้ได้ทั้ง 6 ฉาก** ต่างกันแค่ `?scene=0..5`
 * จอ present เปิดหน้านี้ผ่าน iframe ด้วย `?present=1` (ห้าม fork renderer)
 */
import { createGame, type PlayerScene } from "@py/engine/phaser";
import { Bus, startAutoReload } from "@py/net";
import {
  encode, InputMsg, type PosMsg, ProfileMsg, ReleaseMsg, safeParse, SCENE_IDS,
  type SceneId, screenSubscriptions, SpawnMsg, STYLE_BY_SCENE, SyncMsg, SysCmdMsg,
  clientIds, pidFromTopic, rand4, ScreenStatusMsg, topics, type ActMsg,
} from "@py/protocol";

import { ENV } from "./env.js";
import { mountRegisterUi } from "./register-ui.js";

const params = new URLSearchParams(location.search);
const rawScene = Number(params.get("scene") ?? "0");
const scene: SceneId = (SCENE_IDS as readonly number[]).includes(rawScene)
  ? (rawScene as SceneId)
  : 0;
const present = params.get("present") === "1";
const debug = params.get("debug") === "1" && !present;

document.body.classList.toggle("present", present);
if (!present && params.get("kiosk") !== "0") document.body.classList.add("kiosk");

// จอ kiosk เปิดค้างข้ามวัน — reload ตัวเองเมื่อมี deploy ใหม่ (รอจังหวะไม่มีผู้เล่น)
declare const __BUILD__: string;
startAutoReload(__BUILD__, () => lastPlayers > 0);

const badge = document.getElementById("badge")!;
const badgeText = document.getElementById("badge-text")!;

/**
 * โชว์ป้ายสถานะเฉพาะตอนมีปัญหา — งานออกแบบของฉาก 0 ไม่มีป้ายนี้ และผู้ชม
 * ไม่ควรเห็นคำว่า "connecting" ค้างอยู่มุมจอ · เปิดค้างได้ด้วย ?debug=1
 */
const setBadge = (text: string, online: boolean) => {
  badgeText.textContent = text;
  badge.classList.toggle("online", online);
  badge.hidden = online && !debug;
};
setBadge(`scene ${scene} · เชื่อมต่อ…`, false);

// ---------------- MQTT ----------------

const clientId = clientIds.screen(scene, rand4());

const bus = new Bus({
  url: ENV.mqttUrl,
  username: ENV.mqttUser,
  password: ENV.mqttPass,
  clientId,
  // จอ present ห้ามตั้ง LWT ของ status ด้วย ไม่งั้นตอนปิด iframe จะไปเขียนทับ
  // retained topic ของจอจริงว่า offline
  ...(present
    ? {}
    : {
        will: {
          topic: topics.screenStatus(scene),
          payload: encode({ s: scene, up: false, t: Date.now() }),
        },
      }),
  onStatus: (s) => setBadge(`scene ${scene} · ${s}`, s === "online"),
});

let lastFps = 0;
let lastPlayers = 0;

const { scene: gameScene } = createGame({
  scene,
  present,
  debug,
  parent: "app",
  assetBase: "/assets",
  /**
   * จอ present **ห้ามส่งอะไรกลับขึ้น broker เลย** — มันคือกระจกเงา
   *
   * มันรันฟิสิกส์ชุดเดียวกับจอจริงทุกฉาก ถ้าปล่อยให้ส่งพร้อมจอจริงจะกลายเป็น
   * สองตัวประกาศผลของผู้เล่นคนเดียวกัน: `act` นับคะแนนซ้ำ · `pos`/`edge` สลับกัน
   * เข้ามาจนมือถือเห็นพิกัดกระตุก
   *
   * แต่ **ห้ามเงียบตลอดกาล** (D-31) — เดิมเงียบสนิท ผลคือเปิดหน้ารวมอย่างเดียว
   * แล้ว "ข้ามฉากไม่ได้เลย" เพราะไม่มีใครส่ง pos/edge ให้มือถือตัดสินการข้าม
   * ตอนนี้ดูจาก `screen/{n}/status` (retained + LWT): จอจริงออนไลน์ → เงียบ
   * จอจริงหาย → iframe รับหน้าที่ประกาศแทนเต็มตัว หน้ารวมจึงเล่นเดี่ยวได้
   */
  bridge: {
    publishPos: (msg: PosMsg) => {
      if (present && realScreenUp) return;
      bus.publish(topics.pos(msg.p), encode(msg), 0);
    },
    publishEdge: (pid, side, x, y) => {
      if (present && realScreenUp) return;
      bus.publish(
        topics.edge(pid),
        encode({ p: pid, s: scene, side, x: Math.round(x), y: Math.round(y), t: Date.now() }),
        1,
      );
    },
    publishAct: (msg: Omit<ActMsg, "t">) => {
      if (present && realScreenUp) return;
      bus.publish(topics.act(msg.p), encode({ ...msg, t: Date.now() }), 1);
    },
    onFps: (fps, players) => {
      lastFps = fps;
      lastPlayers = players;
    },
  },
});

const sc: PlayerScene = gameScene;

/**
 * จอจริงของฉากนี้ออนไลน์อยู่ไหม — ใช้เฉพาะโหมด present (ดูคอมเมนต์ bridge)
 * status เป็น retained จึงรู้คำตอบทันทีที่ subscribe · LWT เคลียร์ให้เมื่อจอจริงหลุด
 */
let realScreenUp = false;
if (present) {
  bus.subscribe(topics.screenStatus(scene), (_t, payload) => {
    const m = safeParse(ScreenStatusMsg, payload);
    if (m) realScreenUp = m.up;
  });
}

for (const filter of screenSubscriptions(scene)) {
  bus.subscribe(filter, (topic, payload) => {
    const pid = pidFromTopic(topic);

    if (topic.endsWith("/profile")) {
      if (!pid) return;
      // payload ว่าง = เคลียร์ retained ตอนจบ session
      sc.setProfile(pid, payload.length === 0 ? null : safeParse(ProfileMsg, payload));
      return;
    }

    if (topic.endsWith("/spawn")) {
      // payload ว่าง = มือถือล้าง retained ตอนออกจากฉาก ไม่ใช่คำสั่งให้เกิดตัวละคร
      if (payload.length === 0) {
        if (pid) sc.removePlayer(pid);
        return;
      }
      const m = safeParse(SpawnMsg, payload);
      if (!m) return;
      sc.spawnPlayer({
        pid: m.p, x: m.x, y: m.y,
        style: m.st || STYLE_BY_SCENE[scene],
        auth: m.auth, warp: m.warp,
      });
      return;
    }

    if (topic.endsWith("/input")) {
      const m = safeParse(InputMsg, payload);
      if (m) sc.applyInput(m.p, m);
      return;
    }

    if (topic.endsWith("/release")) {
      const m = safeParse(ReleaseMsg, payload);
      if (m) sc.removePlayer(m.p);
      return;
    }

    if (topic.endsWith("/sync")) {
      const m = safeParse(SyncMsg, payload);
      if (m) sc.applySync(m.p, m);
      return;
    }

    if (topic === topics.sysCmd()) {
      const m = safeParse(SysCmdMsg, payload);
      if (!m) return;
      if (m.cmd === "reload") location.reload();
      else if (m.cmd === "clearPlayers") sc.clearPlayers();
      else if (m.cmd === "setDebug") sc.setDebug(m.v);
    }
  });
}

bus.connect();

// ---------------- heartbeat ----------------

if (!present) {
  // จอ present ห้าม publish status — ถ้าเปิดพร้อมจอจริงจะมีสองตัวแย่งเขียน
  // retained topic เดียวกัน สถานะจะกระพริบ (ARCHITECTURE.md §9)
  const beat = (up: boolean) =>
    bus.publish(
      topics.screenStatus(scene),
      encode({
        s: scene, up, host: location.hostname, build: ENV.build,
        fps: lastFps, players: lastPlayers, t: Date.now(),
      }),
      1,
      true,
    );
  /*
   * ⚠️ แท็บที่ถูกซ่อนต้องประกาศ up:false ทันที — ห้ามรอ LWT
   *
   * เบราว์เซอร์หยุด rAF ของแท็บพื้นหลัง = ฟิสิกส์ของจอนี้หลับสนิท
   * แต่ timer/WS ยังเดิน heartbeat จึงยังตะโกนว่า "ฉันออนไลน์" ทั้งที่จำลอง
   * อะไรไม่ได้เลย → หน้ารวมเห็น status up ก็หุบปากตาม (D-31) สรุปไม่มีใคร
   * ประกาศ pos เลย ผู้เล่นค้าง/ร่วงที่รอยต่อ ("ตกฉากกลับมาเกิดใหม่")
   * จอจริงหน้างานไม่โดนเรื่องนี้เพราะไม่มีวันถูกซ่อน
   */
  const alive = () => !document.hidden;
  const beatTick = () => beat(alive());
  setInterval(beatTick, 5000);
  setTimeout(beatTick, 1200);
  document.addEventListener("visibilitychange", beatTick);

  window.addEventListener("pagehide", () => {
    bus.publish(topics.screenStatus(scene), encode({ s: scene, up: false, t: Date.now() }), 1, true);
  });
}

// ---------------- ฉาก 0: UI ลงทะเบียน ----------------

if (scene === 0) mountRegisterUi(document.getElementById("overlay")!, present, debug);

// ---------------- คำสั่งจากจอ present (iframe แม่) ----------------

/**
 * จอ present สั่งงานผ่าน postMessage แทนที่จะต่อ MQTT เอง
 * — จะได้ไม่ต้องมี credential ชุดที่สองอยู่ในหน้านั้น และไม่เพิ่ม connection เปล่าๆ
 */
window.addEventListener("message", (e: MessageEvent) => {
  const d = e.data as { py?: string; cmd?: string } | null;
  if (!d || d.py !== "sys") return;
  if (d.cmd === "reload") location.reload();
  else if (d.cmd === "clearPlayers") sc.clearPlayers();
});

// ---------------- คีออสก์: กลับมาเองเมื่อพัง ----------------

window.addEventListener("error", (e) => {
  console.error("[screen] error", e.error);
  if (!present) setTimeout(() => location.reload(), 15_000);
});
window.addEventListener("unhandledrejection", (e) => {
  console.error("[screen] rejection", e.reason);
});
